import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CancellationOptions = ({ currentPlan, onCancel, onPause, onDowngrade }) => {
  const [showCancellationForm, setShowCancellationForm] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [showRetentionOffer, setShowRetentionOffer] = useState(false);

  const cancellationReasons = [
    { value: 'too_expensive', label: 'Too expensive' },
    { value: 'not_using', label: 'Not using enough features' },
    { value: 'found_alternative', label: 'Found a better alternative' },
    { value: 'technical_issues', label: 'Technical issues' },
    { value: 'poor_performance', label: 'Poor prediction accuracy' },
    { value: 'missing_features', label: 'Missing features I need' },
    { value: 'temporary_pause', label: 'Temporary financial situation' },
    { value: 'other', label: 'Other reason' }
  ];

  const retentionOffers = [
    {
      id: 'discount_50',
      title: '50% Off Next 3 Months',
      description: 'Continue with your current plan at half price',
      icon: 'Percent',
      color: 'success',
      action: 'Apply Discount'
    },
    {
      id: 'pause_subscription',
      title: 'Pause Subscription',
      description: 'Pause for up to 3 months, resume anytime',
      icon: 'Pause',
      color: 'secondary',
      action: 'Pause Plan'
    },
    {
      id: 'downgrade_plan',
      title: 'Downgrade to Basic',
      description: 'Keep essential features at a lower cost',
      icon: 'ArrowDown',
      color: 'accent',
      action: 'Downgrade'
    }
  ];

  const handleCancellationRequest = () => {
    if (selectedReason === 'too_expensive' || selectedReason === 'temporary_pause') {
      setShowRetentionOffer(true);
    } else {
      setShowCancellationForm(true);
    }
  };

  const handleFinalCancel = () => {
    onCancel({
      reason: selectedReason,
      feedback: feedback,
      effectiveDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString() // 30 days from now
    });
  };

  const handleRetentionOffer = (offerId) => {
    switch (offerId) {
      case 'discount_50':
        // Apply discount logic
        setShowRetentionOffer(false);
        break;
      case 'pause_subscription':
        onPause();
        setShowRetentionOffer(false);
        break;
      case 'downgrade_plan':
        onDowngrade('basic');
        setShowRetentionOffer(false);
        break;
      default:
        break;
    }
  };

  if (showRetentionOffer) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
        <div className="text-center mb-6">
          <Icon name="Heart" size={48} className="text-error mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-card-foreground mb-2">We'd Hate to See You Go!</h2>
          <p className="text-muted-foreground">
            Before you cancel, here are some special offers just for you
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {retentionOffers?.map((offer) => (
            <div key={offer?.id} className="bg-background border border-border rounded-lg p-4 text-center">
              <div className={`w-12 h-12 mx-auto mb-3 rounded-lg bg-${offer?.color}/10 flex items-center justify-center`}>
                <Icon name={offer?.icon} size={24} className={`text-${offer?.color}`} />
              </div>
              <h3 className="font-medium text-foreground mb-2">{offer?.title}</h3>
              <p className="text-sm text-muted-foreground mb-4">{offer?.description}</p>
              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={() => handleRetentionOffer(offer?.id)}
              >
                {offer?.action}
              </Button>
            </div>
          ))}
        </div>
        <div className="flex justify-center space-x-4">
          <Button
            variant="outline"
            onClick={() => setShowRetentionOffer(false)}
          >
            Back to Cancellation
          </Button>
          <Button
            variant="ghost"
            onClick={() => setShowCancellationForm(true)}
            className="text-muted-foreground"
          >
            No Thanks, Continue Cancelling
          </Button>
        </div>
      </div>
    );
  }

  if (showCancellationForm) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-card-foreground mb-2">Cancel Subscription</h2>
          <p className="text-muted-foreground">
            We're sorry to see you go. Please help us improve by sharing your feedback.
          </p>
        </div>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-3">
              Why are you cancelling? *
            </label>
            <div className="space-y-2">
              {cancellationReasons?.map((reason) => (
                <label key={reason?.value} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="cancellation_reason"
                    value={reason?.value}
                    checked={selectedReason === reason?.value}
                    onChange={(e) => setSelectedReason(e?.target?.value)}
                    className="w-4 h-4 text-primary border-border focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">{reason?.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Additional Feedback (Optional)
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e?.target?.value)}
              placeholder="Tell us how we could have served you better..."
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>

          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
              <div>
                <h3 className="font-medium text-warning mb-1">Important Information</h3>
                <ul className="text-sm text-card-foreground space-y-1">
                  <li>• Your subscription will remain active until {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toLocaleDateString()}</li>
                  <li>• You'll lose access to premium features after cancellation</li>
                  <li>• Historical data will be preserved for 90 days</li>
                  <li>• You can reactivate anytime before the end date</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => setShowCancellationForm(false)}
              className="flex-1"
            >
              Keep My Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleFinalCancel}
              disabled={!selectedReason}
              className="flex-1"
            >
              Confirm Cancellation
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-card-foreground">Subscription Options</h2>
        <p className="text-sm text-muted-foreground">Manage your subscription preferences</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-background border border-border rounded-lg p-4 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-lg bg-secondary/10 flex items-center justify-center">
            <Icon name="Pause" size={24} className="text-secondary" />
          </div>
          <h3 className="font-medium text-foreground mb-2">Pause Subscription</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Temporarily pause your plan for up to 3 months
          </p>
          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={onPause}
          >
            Pause Plan
          </Button>
        </div>

        <div className="bg-background border border-border rounded-lg p-4 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="ArrowDown" size={24} className="text-accent" />
          </div>
          <h3 className="font-medium text-foreground mb-2">Downgrade Plan</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Switch to a lower-tier plan with reduced features
          </p>
          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={() => onDowngrade('basic')}
            disabled={currentPlan?.type === 'basic'}
          >
            {currentPlan?.type === 'basic' ? 'Already Basic' : 'Downgrade'}
          </Button>
        </div>

        <div className="bg-background border border-error/20 rounded-lg p-4 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-lg bg-error/10 flex items-center justify-center">
            <Icon name="XCircle" size={24} className="text-error" />
          </div>
          <h3 className="font-medium text-foreground mb-2">Cancel Subscription</h3>
          <p className="text-sm text-muted-foreground mb-4">
            End your subscription and lose access to premium features
          </p>
          <Button
            variant="destructive"
            size="sm"
            fullWidth
            onClick={handleCancellationRequest}
          >
            Cancel Plan
          </Button>
        </div>
      </div>
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div>
            <p className="text-sm font-medium text-card-foreground">Need Help?</p>
            <p className="text-xs text-muted-foreground mt-1">
              Contact our support team before making changes. We're here to help you get the most out of your subscription.
            </p>
            <Button variant="link" size="sm" className="p-0 h-auto mt-2">
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationOptions;